import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, startWith, Subject, switchMap, tap } from 'rxjs';
import { UserGraph } from '../common/models/user-graph.model';
import { BusinessDomainOwnersService } from '../business-domain-owners/business-domain-owners.service';
import { BusinessDomainOwner } from '../business-domain-owners/models/business-domain-owner.model';
import { UserGraphAutocompleteComponent } from '../common/user-graph-autocomplete/user-graph-autocomplete.component';

type AddOwnerForm = {
    owner: FormControl<string | UserGraph>;
};

@Component({
    selector: 'app-business-domain-owners',
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        UserGraphAutocompleteComponent,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './business-domain-owners.component.html',
    styleUrl: './business-domain-owners.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BusinessDomainOwnersComponent implements OnInit {
    public businessDomainId = input<string>('');

    protected owners$!: Observable<BusinessDomainOwner[]>;
    private refreshOwnersSubject = new Subject<void>();
    protected loadingOwners = signal(true);

    protected addOwnerForm: FormGroup<AddOwnerForm>;
    protected ownerIdBeingRemoved = signal('');
    protected ownerIsBeingAdded = signal(false);

    protected removeOwnerButtonClasses = computed(() => {
        const ownerIsBeingRemoved = this.ownerIdBeingRemoved() !== '';
        return {
            'text-danger': !ownerIsBeingRemoved,
            'text-dark': ownerIsBeingRemoved, // To show it as disabled
        };
    });

    public constructor(
        private readonly businessDomainOwnersService: BusinessDomainOwnersService,
        private readonly fb: FormBuilder,
    ) {
        this.addOwnerForm = this.fb.nonNullable.group({
            owner: this.fb.nonNullable.control<string | UserGraph>('', { validators: [Validators.required] }),
        });
    }

    public ngOnInit(): void {
        this.owners$ = this.refreshOwnersSubject.pipe(
            startWith([]),
            switchMap(() => this.businessDomainOwnersService.getBusinessDomainOwners(this.businessDomainId())),
            tap(() => {
                this.loadingOwners.set(false);
            }),
        );
    }

    protected removeOwnerClicked(ownerId: string): void {
        this.ownerIdBeingRemoved.set(ownerId);

        this.businessDomainOwnersService.removeBusinessDomainOwner(this.businessDomainId(), ownerId).subscribe(() => {
            this.ownerIdBeingRemoved.set('');
            this.refreshOwners();
        });
    }

    protected addOwnerClicked(): void {
        const newOwner = this.addOwnerForm.getRawValue().owner;
        if (typeof newOwner === 'string') {
            // this.addOwnerForm.controls.owner.setErrors()
            return;
        }

        this.ownerIsBeingAdded.set(true);

        this.businessDomainOwnersService.addBusinessDomainOwner(this.businessDomainId(), newOwner.id).subscribe(() => {
            this.addOwnerForm.controls.owner.setValue('');
            this.ownerIsBeingAdded.set(false);
            this.refreshOwners();
        });
    }

    private refreshOwners(): void {
        this.loadingOwners.set(true);
        this.refreshOwnersSubject.next();
    }
}
